import { NonJsonResponseError } from "./readJsonResponse";

const FALLBACK = "Something went wrong. Please try again.";

function collectErrorMessages(errors: unknown): string[] {
  if (errors === null || errors === undefined) return [];
  if (typeof errors === "string") {
    const trimmed = errors.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(errors)) {
    return errors.flatMap((entry) => collectErrorMessages(entry));
  }
  if (typeof errors === "object") {
    return Object.values(errors).flatMap((entry) => collectErrorMessages(entry));
  }
  const text = String(errors).trim();
  return text ? [text] : [];
}

export function extractApiResponseErrorMessage(data: unknown, fallback: string = FALLBACK): string {
  if (data === null || data === undefined || typeof data !== "object") return fallback;

  const payload = data as {
    errors?: unknown;
    detail?: unknown;
    error?: unknown;
    message?: unknown;
    messages?: unknown;
  };

  const fromErrors = collectErrorMessages(payload.errors);
  if (fromErrors.length > 0) return fromErrors.join(". ");

  const fromDetail = collectErrorMessages(payload.detail);
  if (fromDetail.length > 0) return fromDetail.join(". ");

  if (typeof payload.error === "string" && payload.error.trim()) {
    return payload.error.trim();
  }

  const fromMessages = collectErrorMessages(payload.messages);
  if (fromMessages.length > 0) return fromMessages.join(". ");

  if (typeof payload.message === "string" && payload.message.trim()) {
    const message = payload.message.trim();
    if (message.toLowerCase() !== "success") return message;
  }

  return fallback;
}

export function getApiErrorMessage(error: unknown, fallback: string = FALLBACK): string {
  if (error instanceof NonJsonResponseError) {
    return error.message;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === "string" && error.trim()) {
    return error;
  }
  if (error !== null && error !== undefined && typeof error === "object") {
    if ("data" in error) {
      const fromRtkData = extractApiResponseErrorMessage(
        (error as { data?: unknown }).data,
        fallback
      );
      if (fromRtkData !== fallback) return fromRtkData;
    }
    return extractApiResponseErrorMessage(error, fallback);
  }
  return fallback;
}

export function getRtkQueryErrorMessage(error: unknown, fallback: string = FALLBACK): string {
  return getApiErrorMessage(error, fallback);
}

export function isUnauthorizedHttpError(status: number): boolean {
  return status === 401 || status === 403;
}

export function getHttpStatus(error: unknown): number | undefined {
  if (error instanceof NonJsonResponseError) {
    return error.status;
  }
  return undefined;
}
